<template>
  <div class="box">
    <sc-form
      ref="form"
      :form="form"
      :rules="rules"
      :hasStyle="false"
      :buttonsStyle="false"
      @submit="onGeneratePreInvoice"
      is-edit>
      <template slot="fields" slot-scope="{ fields }">
        <sc-table
          :data="invoices"
          :total="totalInvoices"
          :per-page="limit"
          :current-page="page"
          :paginated="true"
          :backend-pagination="true"
          :checked-rows.sync="checkedInvoices"
          :row-class="(row) => getClassByMarginStatus(row, 'row')"
          :is-row-checkable="isRowCheckable"
          :has-detailed-visible="({ margin_status }) => !!margin_status"
          @page-change="(newPage) => page = newPage"
          default-sort="id"
          default-sort-direction="asc"
          class="sc-recon-pre-invoice-table"
          checkbox-position="right"
          checkable
          detailed
          scrollable>
          <template slot-scope="{ row }">
            <sc-table-column
              label="N° do Pedido"
              field="order_number">
              <div v-for="so in row.invoice">
                <a
                  target="_blank"
                  :href="`/ng#/order-number/${so.order_number}`"
                  class="sc-recon-pre-invoice-table__table__link">
                  {{ so.order_number }}
                </a>
              </div>

            </sc-table-column>

            <sc-table-column
              label="Docto Transp"
              field="cte.number">
              <router-link
                target="_blank"
                :to="`/recon/pre-invoice-detail/${row.id}?action=cte`"
                class="sc-recon-pre-invoice-table__table__link">
                {{ [row.cte.number, row.cte.serie].join('-') }}
              </router-link>
            </sc-table-column>

            <sc-table-column
              label="CT-e Complementar"
              field="cte_complementary_count">
              {{ row.cte_complementary_count }}
            </sc-table-column>

            <sc-table-column
              label="Docto Fiscal (NF / Dec conteúdo)"
              field="invoice.number">
              <div v-for="so in row.invoice">
                <router-link
                  target="_blank"
                  :to="`/recon/pre-invoice-detail/${row.id}?action=values`"
                  class="sc-recon-pre-invoice-table__table__link">
                  {{ getInvoiceIdentifier(so) }}
                </router-link>
              </div>
            </sc-table-column>

            <sc-table-column
              label="Status"
              field="status.label">
              {{ row.status.label }}
            </sc-table-column>

            <sc-table-column
              label="Valor do pedido"
              field="invoice.value"
              sortable>
              <div v-for="so in row.invoice">
                {{ so.value | currency }}
              </div>
            </sc-table-column>

            <sc-table-column
              label="Frete TMS"
              field="tms_value">
              <router-link
                target="_blank"
                :to="`/recon/pre-invoice-detail/${row.id}?action=values`"
                :class="[
                  'sc-recon-pre-invoice-table__table__link',
                  getClassByMarginStatus(row, 'link'),
                ]">
                {{ row.tms_value | currency }}
              </router-link>
            </sc-table-column>

            <sc-table-column
              label="Frete Docto"
              field="cte_value">
              <router-link
                target="_blank"
                :to="`/recon/pre-invoice-detail/${row.id}?action=values`"
                :class="[
                  'sc-recon-pre-invoice-table__table__link',
                  getClassByMarginStatus(row, 'link'),
                ]">
                {{ row.cte_value | currency }}
              </router-link>
            </sc-table-column>

            <sc-table-column
              label="Valor selecionado"
              field="payment_type"
              :key="`invoices.${row.id}.payment_type`">
              <sc-field
                :prop="`invoices.${row.id}.payment_type`"
                :key="`invoices.${row.id}.payment_type`"
                v-if="shouldShowCustomFields(row)">
                <sc-select
                  v-model="getInvoiceById(fields, row.id).payment_type"
                  @input="selectedValueChange(getInvoiceById(fields, row.id))"
                  expanded>
                  <option
                    v-for="option in getSelectedValueList(row.margin_status)"
                    :value="option.value"
                    :key="option.value">
                    {{ option.label }}
                  </option>
                </sc-select>
              </sc-field>
              <p v-else-if="getInvoiceById(fields, row.id).payment_type">
                {{ getInvoiceById(fields, row.id).payment_type }}
              </p>
              <p v-else>-</p>
            </sc-table-column>

            <sc-table-column
              label="Outro valor"
              :key="`invoices.${row.id}.col_payment_custom_value`">
              <sc-field
                :prop="`invoices.${row.id}.payment_custom_value`"
                :key="`invoices.${row.id}.payment_custom_value`">
                <sc-input-currency
                  :disabled="areCustomFieldsDisabled(row, getInvoiceById(fields, row.id))"
                  v-model="getInvoiceById(fields, row.id).payment_custom_value"/>
              </sc-field>
            </sc-table-column>

            <sc-table-column
              label="Motivo"
              :key="`invoices.${row.id}.col_payment_custom_description`">
              <sc-field
                :prop="`invoices.${row.id}.payment_custom_description`"
                :key="`invoices.${row.id}.payment_custom_description`">
                <sc-input
                  :disabled="areCustomFieldsDisabled(row, getInvoiceById(fields, row.id))"
                  v-model="getInvoiceById(fields, row.id).payment_custom_description" />
              </sc-field>
            </sc-table-column>
          </template>

          <template slot="detail" slot-scope="{ row }">
            <p class="has-text-weight-bold">Status da Margem de divergência:</p>
            <p>{{ row.margin_status.label }}</p>
            </br>
            <div v-if="row.cte_complementary_value">
              <p class="has-text-weight-bold">CT-e Complementar:</p>
              <p>Valor de <b>R$ {{ row.cte_complementary_value }}</b> adicionado através de <b>{{ row.cte_complementary_count }} CT-e Complementares</b></p>
            </div>
          </template>

          <template slot="footer">
            <p v-if="totalInvoices">
              <strong>Total de registros:</strong>
              <span>{{ totalInvoices }}</span>
            </p>
          </template>

          <template slot="bottom-left">
            <sc-table-per-page v-model="limit" />
          </template>
        </sc-table>

        <div class="sc-form__footer columns sc-recon-pre-invoice-table__footer">
          <div class="column">
            <div class="buttons is-right">
              <sc-button
                type="submit"
                theme="primary"
                text="Gerar pré-fatura" />
            </div>
          </div>
        </div>
      </template>
    </sc-form>
  </div>
</template>

<script>
import {
  ScField,
  ScInput,
  ScInputCurrency,
  ScSelect,
  ScButton,
  ScForm,
  ScTable,
  ScTableColumn,
  ScTablePerPage,
  ScToast,
} from '@intelipost-js/secure-components';

import { createNamespacedHelpers } from 'vuex';

import { getInvoiceIdentifier } from '../../../pre-invoice-detail/helpers/invoiceValuesHelper';

const { mapState, mapActions } = createNamespacedHelpers('recon/preInvoiceGeneration');

export default {
  name: 'sc-pre-invoice-generation-table',
  props: {
    form: {
      type: [Object, null],
      required: true,
    },
    invoices: {
      type: Array,
      default: [],
    },
  },
  data() {
    return {
      checkableStatus: [
        'WAITING_FOR_CONCILIATION',
        'RELEASED_FOR_PRE_INVOICE',
      ],
      autoCheckStatus: [
        'EQUAL_VALUES',
        'INSIDE_MARGIN',
      ],
      canShowCustomFieldsStatus: [
        'WAITING_FOR_CONCILIATION',
      ],
      checkedInvoices: [],
      selectedValueList: [
        {
          value: 'TMS',
          label: 'Frete TMS',
        },
        {
          value: 'CTE',
          label: 'Frete Docto',
        },
        {
          value: 'other_value',
          label: 'Outro valor',
        },
      ],
      page: this.$store.state.recon.preInvoiceGeneration.pagination.page,
      limit: this.$store.state.recon.preInvoiceGeneration.pagination.limit,
    };
  },
  watch: {
    page(newPage) {
      const newPagination = { page: newPage, limit: this.limit };
      this.updatePagination(newPagination);
    },
    limit(newLimit) {
      const newPagination = { page: 1, limit: newLimit };
      this.updatePagination(newPagination);
    },
    invoices: {
      deep: true,
      immediate: true,
      handler(newInvoices = []) {
        if (newInvoices.length === 0) {
          this.checkedInvoices = [];
        } else {
          this.checkedInvoices = newInvoices.filter((invoice) => {
            const isCheckable = this.isRowCheckable(invoice);
            return isCheckable ? this.isRowChecked(invoice) : false;
          });
        }
      },
    },
  },
  computed: {
    ...mapState([
      'totalInvoices',
    ]),
    rules() {
      if (this.invoices.length === 0) return null;

      const fieldRules = this.invoices
        .map(({ id, margin_status }) => ({
          [id]: {
            type: 'object',
            required: true,
            fields: {
              payment_custom_value: [{
                trigger: 'input',
                message: 'Informe um valor',
                validator: (rule, value, callback, fields) => {
                  const { payment_type } = fields || {};
                  const { value: marginStatusValue } = margin_status || {};

                  const errors = [];
                  const isChecked = !!this.checkedInvoices.find(item => item.id === id);

                  if (
                    isChecked
                    && (marginStatusValue === 'INSIDE_MARGIN' || marginStatusValue === 'OUT_OF_MARGIN')
                    && payment_type === 'other_value'
                    && !value
                  ) {
                    errors.push(new Error(rule.message, rule.field));
                    callback(errors);
                  }

                  callback();
                },
              }],
              payment_custom_description: [{
                trigger: 'input',
                validator: (rule, value, callback, fields) => {
                  const { payment_type } = fields || {};
                  const { value: marginStatusValue } = margin_status || {};

                  const errors = [];

                  const isChecked = !!this.checkedInvoices.find(item => item.id === id);
                  const isValidMarginStatus = (marginStatusValue === 'INSIDE_MARGIN' || marginStatusValue === 'OUT_OF_MARGIN');
                  const isOtherValue = payment_type === 'other_value';

                  if (isChecked && isValidMarginStatus && isOtherValue) {
                    if (!value || value.length === 0) {
                      errors.push(new Error('Informe um motivo', rule.field));
                      callback(errors);
                    } else if (value && value.length < 15) {
                      errors.push(new Error('Mínimo de 15 caractéres', rule.field));
                      callback(errors);
                    }
                  }

                  callback();
                },
              }],
            },
          },
        }))
        .reduce((itemA, itemB) => ({
          ...itemA,
          ...itemB,
        }), {});

      return {
        invoices: [{
          type: 'object',
          required: true,
          fields: fieldRules,
        }],
      };
    },
  },
  methods: {
    ...mapActions([
      'getPreInvoices',
    ]),
    updatePagination(newPagination) {
      this.$store.commit('recon/preInvoiceGeneration/setFormPagination', newPagination);
      this.getPreInvoices();
    },
    getInvoiceById(fields, id) {
      return fields.invoices[id] || {};
    },
    isRowCheckable(row) {
      const { status } = row || {};
      const { value } = status || {};

      return this.checkableStatus.includes(value);
    },
    isRowChecked(row) {
      const { margin_status } = row || {};
      const { value } = margin_status || {};

      return this.autoCheckStatus.includes(value);
    },
    getClassByMarginStatus(row, type = 'row') {
      const { margin_status } = row || {};
      const { value } = margin_status || {};

      const defaultClassName = 'sc-recon-pre-invoice-table__table__';
      const possibleStatus = {
        EQUAL_VALUES: 'success',
        INSIDE_MARGIN: 'warning',
        OUT_OF_MARGIN: 'danger',
      };

      return possibleStatus[value]
        ? `${defaultClassName}${type} ${defaultClassName}${type}--${possibleStatus[value]}`
        : `${defaultClassName}${type}`;
    },
    selectedValueChange(field) {
      if (field && field.payment_type && field.payment_type !== 'other_value') {
        Object.assign(field, {
          payment_custom_value: null,
          payment_custom_description: null,
        });
      }
    },
    getSelectedValueList(marginStatus) {
      const { value: marginStatusValue } = marginStatus || {};

      return this.selectedValueList.filter((item) => {
        const { value: itemValue } = item || {};

        const isOtherValue = itemValue === 'other_value';
        const isAvailable = ['INSIDE_MARGIN', 'OUT_OF_MARGIN'].includes(marginStatusValue);

        if (!isOtherValue || (isOtherValue && isAvailable)) {
          return item;
        }

        return false;
      });
    },
    shouldShowCustomFields({ status, margin_status }) {
      const { value: statusValue } = status || {};

      return margin_status && statusValue && this.canShowCustomFieldsStatus.includes(statusValue);
    },
    areCustomFieldsDisabled(row, invoice) {
      const { margin_status } = row || {};
      const { value } = margin_status || {};
      const { payment_type } = invoice || {};

      const isOtherValue = payment_type === 'other_value';
      const availableMarginStatus = [
        'INSIDE_MARGIN',
        'OUT_OF_MARGIN',
      ];
      const isAvailable = availableMarginStatus.includes(value);

      return !isAvailable || (isAvailable && !isOtherValue);
    },
    getSelectedInvoices(form) {
      return this.checkedInvoices.map((invoice) => {
        const {
          id,
          status,
          tms_value,
          cte_value,
        } = invoice || {};

        const {
          payment_type,
          payment_custom_value,
          payment_custom_description,
        } = form.invoices[id] || {};

        const possibleValues = {
          TMS: tms_value,
          CTE: cte_value,
          other_value: payment_custom_value,
        };

        const possibleDescriptions = {
          TMS: payment_type,
          CTE: payment_type,
          other_value: payment_custom_description,
        };

        let extraInfo = {};
        if (status && status.value) {
          extraInfo = {
            payment_type,
            payment_custom_value: possibleValues[payment_type],
            payment_custom_description: possibleDescriptions[payment_type],
          };
        }

        return {
          id,
          status: status && status.value ? status.value : null,
          ...extraInfo,
        };
      });
    },
    onGeneratePreInvoice(form) {
      if (this.checkedInvoices.length === 0) {
        ScToast.danger('Selecione um pedido para gerar a pré-fatura');

        return false;
      }

      return this.$emit('generatePreInvoice', this.getSelectedInvoices(form));
    },
    getInvoiceIdentifier,
  },
  components: {
    ScField,
    ScInput,
    ScInputCurrency,
    ScSelect,
    ScButton,
    ScForm,
    ScTable,
    ScTableColumn,
    ScTablePerPage,
    ScToast,
  },
};
</script>

<style lang="scss">
$row-danger: #feecf0;
$row-warning: #fffbeb;
$row-success: #effaf3;

.sc-recon-pre-invoice-table {
  .table-wrapper {
    overflow: auto;
    overflow-y: hidden;
    max-width: 100%;
    border-right: 2px solid #e7e7e7;
  }

  &__notification {
    &--is-warning {
      background-color: #fffbeb;
      color: #947600;
    }
  }

  &__table {
    &__row {
      td {
        white-space: nowrap;

        .field {
          min-width: 130px;
          margin-left: auto;
          margin-right: auto;
        }
      }

      &--danger {
        td {
          background-color: $row-danger;
        }

        &:hover {
          td {
            background-color: darken($row-danger, 1);
          }
        }
      }

      &--warning {
        td {
          background-color: $row-warning;
        }

        &:hover {
          td {
            background-color: darken($row-warning, 2);
          }
        }
      }

      &--success {
        td {
          background-color: $row-success;
        }

        &:hover {
          td {
            background-color: darken($row-success, 2);
          }
        }
      }
    }

    &__link {
      font-weight: bold;

      &--danger {
        color: #e84c3d;
      }

      &--warning {
        color: #bca316;
      }
    }
  }

  &__footer {
    &.sc-form__footer {
      margin: 1.25rem -1.25rem -1.25rem -1.25rem;
      padding: 0.5rem;
    }
  }
}
</style>



// WEBPACK FOOTER //
// node_modules/@intelipost-modules/secure-recon/features/pre-invoice-generation/components/sc-pre-invoice-generation-table/ScPreInvoiceGenerationTable.vue?4d466a10